import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import DigitalAssetActions from 'modules/domain/digitalAsset/duck'
import { push } from 'connected-react-router'
import { generatePath } from 'react-router-dom'
import * as managers from './managers'
import DigitalAssetSelectors from './selectors'
import { DigitalAsset, DigitalAssetMetaData } from 'modules/domain/digitalAsset/types'
import DigitalAssetRoutes from 'views/pages/DigitalAsset/routes'
import { ListResponse } from 'types/api'
import { updateLocationQuery } from 'modules/sagaHelpers'
import { RequestError } from 'modules/errors'

export const fetchList = function* () {
  try {
    let currentPage = yield select(DigitalAssetSelectors.page)
    const uploadCompanyType = yield select(DigitalAssetSelectors.uploadCompanyType)
    const filter = yield select(DigitalAssetSelectors.filter)
    const sorting = yield select(DigitalAssetSelectors.sorting)
    const pageSize = yield select(DigitalAssetSelectors.pageSize)
    let response: ListResponse<DigitalAsset> = yield call(
      managers.getDigitalAssetsList,
      uploadCompanyType,
      filter,
      sorting,
      currentPage,
      pageSize,
    )
    const pages = Math.ceil(response.total_count / pageSize)
    if (pages !== 0 && pages < currentPage) {
      response = yield call(managers.getDigitalAssetsList, uploadCompanyType, filter, sorting, pages, pageSize)
      currentPage = pages
    }

    const { data, page, total_count } = response
    yield put(DigitalAssetActions.listRequestSucceed(data, total_count, page))
    yield call(updateLocationQuery, DigitalAssetRoutes.List, { page: currentPage })
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(DigitalAssetActions.listRequestFailed(errType))
  }
}

export const fetchListNext = function* () {
  try {
    const page = yield select(DigitalAssetSelectors.page)
    const filter = yield select(DigitalAssetSelectors.filter)
    const sorting = yield select(DigitalAssetSelectors.sorting)
    const pageSize = yield select(DigitalAssetSelectors.pageSize)
    const uploadCompanyType = yield select(DigitalAssetSelectors.uploadCompanyType)
    const { data, total_count }: { data: DigitalAsset[]; total_count: number } = yield call(
      managers.getDigitalAssetsList,
      uploadCompanyType,
      filter,
      sorting,
      page,
      pageSize,
    )
    yield put(DigitalAssetActions.listRequestNextSucceed(data, total_count))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(DigitalAssetActions.listRequestNextFailed(errType))
  }
}

export const fetchDigitalAsset = function* ({ payload: id }: ReturnType<typeof DigitalAssetActions.itemRequested>) {
  try {
    const digitalAsset: DigitalAsset = yield call(managers.getDigitalAsset, id)
    yield put(DigitalAssetActions.itemRequestSucceed(digitalAsset))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(DigitalAssetActions.itemRequestFailed(id, errType))
  }
}

export const addDigitalAsset = function* ({
  payload: [dto, file],
}: ReturnType<typeof DigitalAssetActions.addRequested>) {
  try {
    const digitalAsset: DigitalAsset = yield call(managers.addDigitalAsset, dto)
    yield put(DigitalAssetActions.addSucceed(digitalAsset))

    if (digitalAsset.id) {
      yield call(managers.uploadDigitalAssetFile, digitalAsset.id, file)
    }

    yield put(push(generatePath(DigitalAssetRoutes.List)))
  } catch (e) {
    const errType = e instanceof RequestError ? e.type : 'unknown'
    const detail = e instanceof RequestError ? e.error : ''
    yield put(DigitalAssetActions.addFailed(errType, detail))
  }
}

export const updateDigitalAsset = function* ({
  payload: [id, dto],
}: ReturnType<typeof DigitalAssetActions.updateRequested>) {
  try {
    const digitalAsset: DigitalAsset = yield call(managers.updateDigitalAsset, id, dto)
    yield put(DigitalAssetActions.updateSucceed(digitalAsset))
  } catch (e) {
    const type = e instanceof RequestError ? e.type : 'unknown'
    const detail = e instanceof RequestError ? e.error : ''
    yield put(DigitalAssetActions.updateFailed(id, type, detail))
  }
}

export const softDeleteDigitalAssets = function* ({
  payload,
}: ReturnType<typeof DigitalAssetActions.softDeleteRequested>) {
  try {
    yield call(managers.softDeleteDigitalAssets, payload)
    yield put(DigitalAssetActions.softDeleteSucceed(payload))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(DigitalAssetActions.softDeleteFailed(payload, errType))
  }
}

export const restoreDigitalAssets = function* ({ payload }: ReturnType<typeof DigitalAssetActions.restoreRequested>) {
  try {
    yield call(managers.restoreDigitalAssets, payload)
    yield put(DigitalAssetActions.restoreSucceed(payload))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(DigitalAssetActions.restoreFailed(payload, errType))
  }
}

export const permanentDeleteDigitalAssets = function* ({
  payload,
}: ReturnType<typeof DigitalAssetActions.permanentDeleteRequested>) {
  try {
    yield call(managers.permanentDeleteDigitalAssets, payload)
    yield put(DigitalAssetActions.permanentDeleteSucceed(payload))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(DigitalAssetActions.permanentDeleteFailed(payload, errType))
  }
}

export const downloadDigitalAssetFile = function* ({
  payload: [id, documentName],
}: ReturnType<typeof DigitalAssetActions.downloadFileRequested>) {
  try {
    yield call(managers.downloadDigitalAssetFile, id, documentName)
    yield put(DigitalAssetActions.downloadFileSucceed(id))
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('File download error:', err)
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(DigitalAssetActions.downloadFileFailed(id, errType))
  }
}

export const addLinkAsset = function* ({ payload: dto }: ReturnType<typeof DigitalAssetActions.addLinkRequested>) {
  try {
    const digitalAsset: DigitalAsset = yield call(managers.addLinkAsset, dto)
    yield put(DigitalAssetActions.addLinkSucceed(digitalAsset))
    yield put(push(generatePath(DigitalAssetRoutes.List)))
  } catch (e) {
    const errType = e instanceof RequestError ? e.type : 'unknown'
    const detail = e instanceof RequestError ? e.error : ''
    yield put(DigitalAssetActions.addLinkFailed(errType, detail))
  }
}

export const selectedDigitalAssetDraft = function* ({
  payload,
}: ReturnType<typeof DigitalAssetActions.selectedFileDraftRequested>) {
  yield put(DigitalAssetActions.selectedFileDraftSetter(payload))
}

export const fetchDigitalAssetMetadata = function* (props) {
  try {
    let id
    if (Array.isArray(props.payload)) {
      id = props.payload[0]
    } else {
      id = props.payload.id
    }
    const digitalAssetMetaData: DigitalAssetMetaData = yield call(managers.getDigitalAssetMetadata, id)
    yield put(DigitalAssetActions.itemMetaDataRequestSucceed(digitalAssetMetaData))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(DigitalAssetActions.itemMetaDataRequestFailed(errType))
  }
}

const DigitalAssetsSaga = function* () {
  yield all([
    takeLatest(DigitalAssetActions.itemRequested.type, fetchDigitalAsset),

    takeLatest(DigitalAssetActions.listRequested.type, fetchList),
    takeLatest(DigitalAssetActions.filterUpdated.type, fetchList),
    takeLatest(DigitalAssetActions.sortingUpdated.type, fetchList),
    takeLatest(DigitalAssetActions.filterHasBeenReset.type, fetchList),
    takeLatest(DigitalAssetActions.sortingHasBeenReset.type, fetchList),

    takeLatest(DigitalAssetActions.listRequestedNext.type, fetchListNext),

    takeLatest(DigitalAssetActions.addRequested.type, addDigitalAsset),
    takeLatest(DigitalAssetActions.updateRequested.type, updateDigitalAsset),
    takeLatest(DigitalAssetActions.softDeleteRequested.type, softDeleteDigitalAssets),
    takeLatest(DigitalAssetActions.restoreRequested.type, restoreDigitalAssets),
    takeLatest(DigitalAssetActions.permanentDeleteRequested.type, permanentDeleteDigitalAssets),
    takeLatest(DigitalAssetActions.downloadFileRequested.type, downloadDigitalAssetFile),
    takeLatest(DigitalAssetActions.selectedFileDraftRequested.type, selectedDigitalAssetDraft),

    takeLatest(DigitalAssetActions.addLinkRequested.type, addLinkAsset),

    takeLatest(DigitalAssetActions.itemMetaDataRequested.type, fetchDigitalAssetMetadata),
  ])
}

export default DigitalAssetsSaga
